// /app/frontend/src/pages/risk/DPIAIntakeManagementPage.jsx
// DPO page for creating and managing DPIA intake requests
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { dpiaIntakeAPI, entitiesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { CreateTempGuestDialog } from '../../components/guest/CreateTempGuestDialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Mail, MoreHorizontal, Trash2, RefreshCw, ExternalLink,
  Inbox, Clock, CheckCircle, AlertCircle, Copy, Eye
} from 'lucide-react';

export default function DPIAIntakeManagementPage() {
  const navigate = useNavigate();
  const { isDPO, user, isComplianceReadOnly: isReadOnly, isEntityAdmin, isClientAdmin } = useAuth();
  const { selectedEntityId, selectedDepartmentId } = useGlobalContext();

  const [intakes, setIntakes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchData();
  }, [filterStatus, selectedEntityId, selectedDepartmentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [intakesRes, entitiesRes] = await Promise.all([
        dpiaIntakeAPI.getList(selectedEntityId || undefined, filterStatus === 'all' ? null : filterStatus, selectedDepartmentId || undefined),
        (isDPO || isClientAdmin) ? entitiesAPI.getAll(isClientAdmin ? user?.client_id : undefined) : Promise.resolve({ data: [] }),
      ]);
      setIntakes(intakesRes.data);
      if (isDPO || isClientAdmin) setEntities(entitiesRes.data);
      else if (isEntityAdmin && user?.entity_id) setEntities([{ id: user.entity_id, entity_name: user.entity_name || 'Your Entity' }]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load intake requests');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (intake) => {
    try {
      await dpiaIntakeAPI.resendEmail(intake.id);
      toast.success(`Email resent to ${intake.client_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this intake request?')) return;
    try {
      await dpiaIntakeAPI.delete(id);
      toast.success('Intake request deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleCopyLink = (token) => {
    const link = `${window.location.origin}/intake/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const handleSendTestEmail = async () => {
    try {
      const result = await dpiaIntakeAPI.sendTestEmail();
      toast.success(`Test email sent: ${result.data.message}`);
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'in_progress':
        return <Badge variant="warning"><RefreshCw className="h-3 w-3 mr-1" />In Progress</Badge>;
      case 'completed':
        return <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'expired':
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Expired</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity?.entity_name || 'Unknown';
  };

  const stats = {
    total: intakes.length,
    pending: intakes.filter(i => i.status === 'pending').length,
    inProgress: intakes.filter(i => i.status === 'in_progress').length,
    completed: intakes.filter(i => i.status === 'completed').length,
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="dpia-intake-management">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">DPIA Intake Management</h1>
            <p className="text-muted-foreground">Send and track intake questionnaires</p>
          </div>
          {!isReadOnly && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleSendTestEmail} data-testid="test-email-btn">
                <Mail className="h-4 w-4 mr-2" />
                Send Test Email
              </Button>
              <Button onClick={() => setDialogOpen(true)} className="bg-teal-500 hover:bg-teal-600" data-testid="new-intake-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Intake Request
              </Button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-sm text-muted-foreground">Total Requests</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
              <p className="text-sm text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
              <p className="text-sm text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email Sent</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>DPIA</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : intakes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="p-0 border-0">
                      <div className="py-12 px-6 text-center">
                        <Inbox className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <h3 className="font-semibold mb-2">No intake requests yet</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Send a questionnaire to clients to start the DPIA process
                        </p>
                        {!isReadOnly && (
                          <Button onClick={() => setDialogOpen(true)} className="bg-teal-500 hover:bg-teal-600">
                            <Plus className="h-4 w-4 mr-2" />
                            New Intake Request
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  intakes.map((intake) => (
                    <TableRow key={intake.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{intake.client_name}</p>
                          <p className="text-xs text-muted-foreground">{intake.client_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getEntityName(intake.entity_id)}</TableCell>
                      <TableCell>{getStatusBadge(intake.status)}</TableCell>
                      <TableCell>
                        {intake.email_sent ? (
                          <Badge variant="outline" className="text-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />Sent
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600">
                            <Clock className="h-3 w-3 mr-1" />Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(intake.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(intake.expires_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {intake.created_dpia_id ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 h-auto text-teal-600"
                            onClick={() => navigate(`/risk/dpia/${intake.created_dpia_id}`)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!intake.guest_id && (
                              <DropdownMenuItem onClick={() => handleCopyLink(intake.token)}>
                                <Copy className="h-4 w-4 mr-2" />Copy Link
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => intake.guest_id
                                ? navigate(`/risk/dpia-intake/${intake.id}/form`)
                                : window.open(`/intake/${intake.token}`, '_blank')
                              }
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />Open Form
                            </DropdownMenuItem>
                            {!isReadOnly && (
                              <>
                                {intake.status !== 'completed' && (
                                  <DropdownMenuItem onClick={() => handleResendEmail(intake)}>
                                    <Mail className="h-4 w-4 mr-2" />Resend Email
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => handleDelete(intake.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create Invite Dialog */}
        <CreateTempGuestDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          defaultModule="dpia_intake"
          entities={entities}
          lockedEntityId={isEntityAdmin ? user?.entity_id : null}
          onCreated={fetchData}
        />
      </div>
    </DashboardLayout>
  );
}
